import {StatusLabelInfo} from "./medicationInformation/MedicationInfoSection";
import React from "react";
import {AdministrationWarningIcons, DrugLabel} from "./medicationInformation/DrugDetails";
import {Text} from "../../../kit/Text";
import {Grid} from "../../../kit/Grid";
import styled from "styled-components";
import {Image} from "../../../kit/Image";
import {HSChartDose, HSDrug, HSPackedMedication, HSPatient, NimAvailableDrugDto} from "server-openapi";
import {DrugUtils} from "../../../syncstream/utils/DrugUtils";
import {useApiUtils} from "../../../syncstream/utils/hooks/useApiUtils";
import {useStore} from "../../../core/storage/hooks/UseStore";
import itiriri from "itiriri";
import {useSyncCenter} from "../../../syncstream/SyncCenterProvider";
import {SecondCheckBar} from "./medicationInformation/MedicationInformationBox";
import {AdministrationPermissions, SecondCheckData} from "./MedicationListsTabbedRouter/MedicationListsTabbedRouter";
import {getConsentDescription} from "../../../core/utils/consentUtils";

interface MedicationInformationLabelProps {
  patient: HSPatient;
  drug?: HSDrug;
  drugHsId?: number;
  statusLabel?: StatusLabelInfo;
  packedMedication: HSPackedMedication;
  facilityGroupId: number;
  nimAvailableDrug?: NimAvailableDrugDto;
  isPsychotropicConsent?: boolean;
  route?: string;
  chartItemDose?: HSChartDose;
  selectedDate: Date;
  administrationPermissions: AdministrationPermissions;
  secondCheckData?: SecondCheckData;
}

const MedicationInformationLabel = (props: MedicationInformationLabelProps) => {
  const apiUtils = useApiUtils();
  const isTimeCritical = props.packedMedication && (props.packedMedication as HSPackedMedication).timeCritical;
  const route = props.route ?? props.nimAvailableDrug?.route;
  const administrationWarnings = DrugUtils.getAdministrationWarnings(
    props.drug,
    props.packedMedication,
    apiUtils.patients.getHSMedicationFromPackedMedication(props.patient, props.packedMedication),
  );
  const services = useSyncCenter();
  const drugStore = useStore(services.drugs.store).store;
  const drugs = itiriri(drugStore.values()).toArray();

  const drug = drugs.find((drug) => drug.hsId === (props.nimAvailableDrug?.drugHsId ?? props.packedMedication?.drugHsId));
  const medication = apiUtils.patients.getHSMedicationFromPackedMedication(props.patient, props.packedMedication);
  const consentDescription = getConsentDescription(medication?.psychotropicConsent, medication?.consent);

  return (
    <MainContainer>
      <Grid cols={2} gap={1} colsTemplate={'0.5fr 1fr'}>
        <DrugInfoContainer>
          {props.statusLabel && (
            <StatusLabel style={{color: props.statusLabel.colour, background: props.statusLabel.backgroundColour}}>
              {props.statusLabel.label}
            </StatusLabel>
          )}
          {drug?.imageUri && <DrugImage src={drug.imageUri} facilityGroupId={props.facilityGroupId}/>}
        </DrugInfoContainer>

        <DrugInfoContainer>
          {drug && (
            <>
              <DrugLabel drug={drug} isCeased={administrationWarnings.isCeasedMedication}
                         isTimeCritical={isTimeCritical}
                         isPsychotropicConsent={props.isPsychotropicConsent!}/>
              <Line></Line>
            </>
          )}
          <Text>{drug?.genericName && `(${drug.genericName})`}</Text>
          <Text>
            {props.packedMedication.directions} {route && `[${route}]`}
          </Text>
          {props.chartItemDose?.reasonForPrescribing && (
            <Text>Reason for prescribing: {props.chartItemDose?.reasonForPrescribing}</Text>
          )}
          {props.chartItemDose?.indications && <Text>Indications: {props.chartItemDose?.indications}</Text>}
          {consentDescription && <Text>{consentDescription}</Text>}
          <WarningContainer>
            <AdministrationWarningIcons administrationWarnings={administrationWarnings}/>
          </WarningContainer>
        </DrugInfoContainer>
      </Grid>
      <SecondCheckBar {...props} displayAllActivity={false}/>
    </MainContainer>
  );
}

const MainContainer = styled.div`
    background-color: rgb(255, 255, 255);
    border: 1px solid #FFFFFF;
    border-radius: 3px;
    width: 100%;
`

const DrugImage = styled(Image)`
    object-fit: cover;
    width: 70%;
    height: auto;
    margin: 1em;
`;

const DrugInfoContainer = styled.div`
    display: flex;
    padding: 1em;
    flex-direction: column;
    justify-content: center;
    word-wrap: break-word;
    overflow: auto;
  color: black;
  

    &::-webkit-scrollbar {
        display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
`;

const WarningContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.2em;
    padding: 0.2em;
`;

const StatusLabel = styled.div`
    text-align: center;
    padding: 0.2em;
    margin-bottom: 0.5em;
`;

const Line = styled.hr`
    border: 1px solid rgb(0, 0, 0);
    margin: .25em 0;
`

export default MedicationInformationLabel;